import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Landing from "./pages/Landing";
import About from "./pages/About";
import Terms from "./pages/legal/Terms";
import RefundPolicy from "./pages/legal/RefundPolicy";
import Privacy from "./pages/legal/Privacy";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import Lesson from "./pages/Lesson";
import Decks from "./pages/Decks";
import DeckStudy from "./pages/DeckStudy";
import Tests from "./pages/Tests";
import TestRunner from "./pages/TestRunner";
import Dictionary from "./pages/Dictionary";
import Favorites from "./pages/Favorites";
import Settings from "./pages/Settings";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminLessons from "./pages/admin/AdminLessons";
import AdminDecks from "./pages/admin/AdminDecks";
import AdminCards from "./pages/admin/AdminCards";
import AdminTests from "./pages/admin/AdminTests";
import AdminQuestions from "./pages/admin/AdminQuestions";
import AdminBrand from "./pages/admin/AdminBrand";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminBankSettings from "./pages/admin/AdminBankSettings";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Sonner position="top-right" richColors />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:slug" element={<CourseDetail />} />
                <Route path="/courses/:slug/lessons/:lessonSlug" element={<Lesson />} />
                <Route path="/decks" element={<Decks />} />
                <Route path="/decks/:slug" element={<DeckStudy />} />
                <Route path="/tests" element={<Tests />} />
                <Route path="/tests/:slug" element={<TestRunner />} />
                <Route path="/dictionary" element={<Dictionary />} />
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminUsers />} />
                  <Route path="packages" element={<AdminPackages />} />
                  <Route path="courses" element={<AdminCourses />} />
                  <Route path="courses/:courseId/lessons" element={<AdminLessons />} />
                  <Route path="decks" element={<AdminDecks />} />
                  <Route path="decks/:deckId/cards" element={<AdminCards />} />
                  <Route path="tests" element={<AdminTests />} />
                  <Route path="tests/:testId/questions" element={<AdminQuestions />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="bank-settings" element={<AdminBankSettings />} />
                  <Route path="brand" element={<AdminBrand />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
